(() => {
  function install() {
    const controller = window.PXUTheme?.cartDrawer;
    const drawer = controller?.drawer;
    const recoveryTemplate = document.querySelector('[data-cart-recovery-template]');
    if (!drawer || !recoveryTemplate || controller.hardened) return;
    controller.hardened = true;
    const registerEvent = controller.events.register.bind(controller.events);
    controller.events.register = (element, event, listener) => {
      if (element === window && event === 'keydown') {
        for (const registered of [...controller.events.events]) {
          if (registered.el === window && registered.event === 'keydown') controller.events.unregister(registered);
        }
      }
      return registerEvent(element, event, listener);
    };

    let mutating = false;
    let stale = false;
    let loadVersion = 0;
    let loadController;
    let noteDraft;
    let noteDirty = false;
    const controlStates = new Map();
    const controls = '[data-quantity-input], [data-quantity-minus], [data-quantity-plus], [data-remove-item], [name="checkout"]';
    const initializeDrawer = controller.initDrawer.bind(controller);

    function wireQuantities() {
      controller.quantitySelectors?.forEach(selector => {
        if (selector.cartHardeningWired) return;
        selector.cartHardeningWired = true;
        const unload = selector.unload.bind(selector);
        selector.onChange = () => {
          clearTimeout(selector.cartHardeningTimer);
          selector.cartHardeningTimer = setTimeout(() => {
            if (!selector.input.isConnected) return;
            const key = selector.input.closest('[data-cart-line-key]')?.dataset.cartLineKey;
            changeItem(selector.input.dataset.lineId, selector.input.value, key);
          }, 300);
        };
        selector.unload = () => {
          clearTimeout(selector.cartHardeningTimer);
          unload();
        };
      });
    }

    controller.initDrawer = forceOpen => {
      initializeDrawer(forceOpen);
      wireQuantities();
    };
    wireQuantities();

    function setBusy(busy) {
      drawer.setAttribute('aria-busy', String(busy));
      if (busy) {
        drawer.querySelectorAll(controls).forEach(control => {
          if (!controlStates.has(control)) controlStates.set(control, control.disabled);
          control.disabled = true;
        });
      } else {
        for (const [control, disabled] of controlStates) {
          if (control.isConnected) control.disabled = disabled || (stale && control.name === 'checkout');
        }
        controlStates.clear();
        if (stale) drawer.querySelectorAll('[name="checkout"]').forEach(control => { control.disabled = true; });
      }
    }

    function showRecovery(error) {
      stale = true;
      drawer.querySelector('[data-cart-recovery]')?.remove();
      const recovery = recoveryTemplate.content.cloneNode(true);
      const message = recovery.querySelector('[data-cart-recovery-message]');
      message.textContent = typeof error?.description === 'string' ? error.description : '';
      message.hidden = !message.textContent;
      drawer.append(recovery);
      drawer.querySelectorAll('[name="checkout"]').forEach(control => { control.disabled = true; });
    }

    async function request(url, options = {}) {
      const abort = options.signal ? null : new AbortController();
      const timer = abort ? setTimeout(() => abort.abort(), 15000) : null;
      try {
        const response = await fetch(url, { ...options, signal: options.signal || abort.signal });
        if (!response.ok) {
          let error;
          try { error = await response.json(); } catch (_) { error = {}; }
          throw error;
        }
        return response;
      } finally {
        clearTimeout(timer);
      }
    }

    drawer.addEventListener('input', event => {
      if (event.target.name !== 'note') return;
      noteDraft = event.target.value;
      noteDirty = true;
    });

    drawer.addEventListener('click', event => {
      if (event.target.closest('[data-cart-retry]')) controller.loadDrawer(true);
    });

    drawer.addEventListener('submit', event => {
      if (!mutating && !stale && !loadController) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);

    controller.loadDrawer = async (forceOpen = false, atcButton = '') => {
      const version = ++loadVersion;
      loadController?.abort();
      const abort = new AbortController();
      loadController = abort;
      const timer = setTimeout(() => abort.abort(), 15000);
      const wasOpen = controller.overlay.classList.contains('active');
      const focusId = drawer.contains(document.activeElement) ? document.activeElement.id : null;
      const note = drawer.querySelector('[name="note"]');
      const termsChecked = drawer.querySelector('[data-tos]')?.checked;
      if (note && noteDirty) noteDraft = note.value;
      setBusy(true);
      try {
        const url = new URL(window.PXUTheme.routes.cart_url, location.origin);
        url.searchParams.set('view', '_drawer');
        const response = await request(url, { signal: abort.signal, cache: 'no-store' });
        const documentHtml = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = documentHtml.querySelector('template[data-html="content"]');
        const data = JSON.parse(documentHtml.querySelector('template[data-data]')?.content.textContent || 'null');
        if (!content || !Number.isInteger(data?.item_count)) throw new Error('Invalid drawer response');
        if (version !== loadVersion) return false;
        controller.quantitySelectors?.forEach(selector => selector.unload());
        for (const registered of [...controller.events.events]) {
          if ((registered.el instanceof Node && drawer.contains(registered.el)) || registered.el === controller.overlay ||
              registered.el === controller.drawerOpenButton || registered === controller.closeEscEvent) {
            controller.events.unregister(registered);
          }
        }
        drawer.innerHTML = content.innerHTML;
        const newNote = drawer.querySelector('[name="note"]');
        if (newNote && noteDirty) newNote.value = noteDraft;
        const newTerms = drawer.querySelector('[data-tos]');
        if (newTerms && termsChecked !== undefined) newTerms.checked = termsChecked;
        stale = false;
        controller.initDrawer(forceOpen || wasOpen);
        controller.updateCartCount(data.item_count);
        if (atcButton) window.modal?.close();
        if (focusId) requestAnimationFrame(() => drawer.querySelector(`#${CSS.escape(focusId)}`)?.focus({ preventScroll: true }));
        return true;
      } catch (error) {
        if (version === loadVersion) {
          console.error('Cart drawer refresh failed:', error);
          showRecovery(error);
          if (forceOpen && controller.animateDrawer) controller.openDrawer();
        }
        return false;
      } finally {
        clearTimeout(timer);
        if (version === loadVersion) {
          loadController = null;
          if (!mutating) setBusy(false);
        }
        if (atcButton) atcButton.classList.remove('cart-drawer--loading');
      }
    };

    async function changeItem(line, quantity, expectedKey) {
      if (mutating || stale || loadController) return false;
      const input = Array.from(drawer.querySelectorAll('[data-quantity-input]')).find(input => input.dataset.lineId === String(line));
      const key = input?.closest('[data-cart-line-key]')?.dataset.cartLineKey;
      if (expectedKey && expectedKey !== key) return false;
      if (!key || String(quantity).trim() === '' || !Number.isInteger(Number(quantity)) || Number(quantity) < 0) {
        input?.reportValidity();
        return false;
      }
      if (Number(quantity) > 0 && !input.checkValidity()) {
        input.reportValidity();
        return false;
      }
      mutating = true;
      setBusy(true);
      try {
        const root = window.Shopify.routes.root;
        await request(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: Number(quantity) })
        });
        return await controller.loadDrawer();
      } catch (error) {
        input.value = input.defaultValue;
        showRecovery(error);
        return false;
      } finally {
        mutating = false;
        setBusy(false);
      }
    }

    controller.updateItem = changeItem;
    controller.removeItem = line => changeItem(line, 0);

    controller.addItem = async button => {
      const form = button.form;
      if (!form || button.disabled || mutating || stale || loadController || !form.reportValidity()) return false;
      const body = new FormData(form);
      const disabled = button.disabled;
      mutating = true;
      button.disabled = true;
      button.classList.add('cart-drawer--loading');
      setBusy(true);
      try {
        await request(`${window.Shopify.routes.root}cart/add.js`, { method: 'POST', body });
        return await controller.loadDrawer(true, button);
      } catch (error) {
        const recipient = form.querySelector('[data-recipient-form]');
        if (recipient && error?.errors?.email) recipient.classList.add('recipient-form--has-errors');
        showRecovery(error);
        if (controller.animateDrawer) controller.openDrawer();
        return false;
      } finally {
        mutating = false;
        button.disabled = disabled;
        button.classList.remove('cart-drawer--loading');
        setBusy(false);
      }
    };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', install, { once: true });
  else install();
})();
